/**
 * V2 icons — a port of the Claude Design V2 package's `portal-free-app/icons.jsx` (PI + PIcon).
 *
 * Source: refactor/design-sync/portal-free-app-v2/design_handoff_portal_v2/portal-free-app/icons.jsx
 * The base set (`RIC`) is the app's shipped `hubRail/railIcons.tsx`, itself a port of the design's
 * `nav-rail/icons.jsx`, so rail glyphs are not duplicated here. Path strings below are copied verbatim.
 * `pc` is the design file's own circle helper (`__pc`), kept as written.
 */
import React from 'react';
import { RIC, RailIcon } from '../hubRail/railIcons';

const pc = (cx: number, cy: number, r: number): string =>
  `M${cx - r} ${cy}a${r} ${r} 0 1 0 ${r * 2} 0a${r} ${r} 0 1 0 -${r * 2} 0`;

export const PI = {
  roadmap:   'M4 19c1-5 4-6 8-6s7-1 8-6|' + pc(4, 19, 2) + '|' + pc(12, 13, 2) + '|' + pc(20, 7, 2),      // route through three nodes
  workflows: RIC.docs,                                                                                     // same concept as staff Documentation
  sops:      'M6 3h9l4 4v14H6z|M15 3v4h4|M9 12.5l2 2 4-4.5|M9 17.5h6',                                            // page with a checked rule
  eos:       RIC.secEOS,
  injector:  'M13 4h7v16h-7|M3 12h11|M10 8l4 4-4 4',                                                              // arrow entering a box
  settings:  RIC.settings,
  logout:    RIC.logout,
  kebab:     'M5 12h.01|M12 12h.01|M19 12h.01',
  plus:      'M12 5v14|M5 12h14',
  close:     'M18 6L6 18|M6 6l12 12',
  check:     'M5 12.5l4.5 4.5L19 7',
  search:    RIC.search,
  chevDown:  'M6 9l6 6 6-6',
  chevUp:    'M18 15l-6-6-6 6',
  chevRight: 'M9 6l6 6-6 6',
  chevLeft:  'M15 6l-6 6 6 6',
  filter:    'M4 6h16|M7 12h10|M10 18h4',
  copy:      'M9 9h11v11H9z|M5 15V4h11',
  link:      'M10 14a4 4 0 005.7 0l3-3a4 4 0 00-5.7-5.7l-1 1|M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 005.7 5.7l1-1',
  external:  'M14 4h6v6|M20 4l-9 9|M18 13v6H5V6h6',
  eye:       'M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z|' + pc(12, 12, 3),
  eyeOff:    'M3 3l18 18|M10.6 10.6a2 2 0 002.8 2.8|M6.5 6.7C4 8.4 2 12 2 12s3.5 7 10 7c1.6 0 3-.4 4.3-1|M9.9 5.2A10 10 0 0112 5c6.5 0 10 7 10 7a15 15 0 01-2.6 3.4',
  trash:     'M5 7h14|M9 7V5h6v2|M7 7l1 12h8l1-12|M10 11v5M14 11v5',
  archive:   'M3 5h18v4H3z|M5 9v11h14V9|M10 13h4',
  tag:       'M3 3h8l10 10-8 8L3 11z|M7.5 7.5h.01',
  grid:      'M3 3h7v7H3z|M14 3h7v7h-7z|M3 14h7v7H3z|M14 14h7v7h-7z',
  table:     'M3 4h18v16H3z|M3 10h18|M3 15h18|M9 4v16',
  board:     'M3 4h5v16H3z|M10 4h5v11h-5z|M17 4h4v7h-4z',
  key:       'M15 7a4 4 0 11-2.5 7.1L11 15.6V18H8.5v2.5H6V18l5-5.1A4 4 0 0115 7z|' + pc(15.5, 8.5, 1),
  lock:      'M5 11h14v10H5z|M8 11V7a4 4 0 018 0v4|M12 15v3',
  mail:      RIC.mail,
  user:      pc(12, 8, 4) + '|M4 21a8 8 0 0116 0',
  users:     pc(9, 8, 3.5) + '|M2 20a7 7 0 0114 0|' + pc(17, 9, 3) + '|M17 13a5 5 0 015 5.5',
  shield:    'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z|M9 12l2 2 4-4',
  spark:     RIC.sparkle,
  send:      RIC.newsletters,
  grip:      RIC.grip,
  pencil:    'M4 20h4l11-11-4-4L4 16z|M13 7l4 4',
  download:  'M12 3v12|M7 10l5 5 5-5|M4 21h16',
  upload:    'M12 15V3|M7 8l5-5 5 5|M4 21h16',
  refresh:   'M20 12a8 8 0 01-14.5 4.6|M4 12A8 8 0 0118.5 7.4|M18.5 3v4.4H14|M5.5 21v-4.4H10',
  play:      'M7 5v14l11-7z',
  building:  RIC.clients,
  calendar:  RIC.calendar,
  clock:     pc(12, 12, 8) + '|M12 8v4.4l3 1.8',
  warn:      'M12 3l10 18H2z|M12 10v4|M12 18h.01',
  info:      pc(12, 12, 9) + '|M12 11v5|M12 8h.01',
  minus:     'M5 12h14',
  collapse:  RIC.collapse,
  expand:    RIC.expand,
  video:     'M3 6h13v12H3z|M16 10l5-3v10l-5-3',
  bolt:      'M13 2L4 14h7l-1 8 9-12h-7z',
  chart:     'M4 20h16|M6.5 20v-6|M11 20V9|M15.5 20v-9|M20 20v-14',
  sort:      'M7 4v16|M4 17l3 3 3-3|M17 20V4|M14 7l3-3 3 3',
  sliders:   'M4 7h10|M18 7h2|M4 17h4|M12 17h8|' + pc(16, 7, 2) + '|' + pc(10, 17, 2),
  inbox:     'M3 13h5l2 3h4l2-3h5|M3 13l2.5-8h13L21 13v7H3z',
  reply:     'M9 17l-5-5 5-5|M4 12h9a7 7 0 017 7v1',
  home:      RIC.secGeneral,
  dollar:    'M12 2v20|M17 6.5c0-2-2.2-3.5-5-3.5s-5 1.5-5 3.5 2 3 5 3.5 5 1.5 5 3.5-2.2 3.5-5 3.5-5-1.5-5-3.5',
  hash:      'M4 9h16|M4 15h16|M10 3l-2 18|M16 3l-2 18',
  terminal:  'M4 5h16v14H4z|M8 9l3 3-3 3|M13 15h4',
  desktop:   'M3 4h18v12H3z|M8 20h8|M12 16v4',
  globe:     pc(12, 12, 9) + '|M3 12h18|M12 3a14 14 0 010 18|M12 3a14 14 0 000 18',
  arrowRight:'M5 12h14|M13 6l6 6-6 6',
  arrowLeft: 'M19 12H5|M11 6l-6 6 6 6',
  diamond:   'M12 3l9 9-9 9-9-9z',
  // Center Console wave 2 (CC-B) — verbatim from the canvas's Prototype3 ICON set.
  phone:     'M5 4h4l2 5-2.5 1.5a11 11 0 005 5L15 13l5 2v4a2 2 0 01-2 2A16 16 0 013 6a2 2 0 012-2z',
  receipt:   'M6 3h12v18l-3-2-3 2-3-2-3 2z|M9 8h6|M9 12h6|M9 16h3',
  pie:       'M21 12A9 9 0 1112 3v9z|M15 3.5a9 9 0 015.5 5.5H15z',
  trend:     'M3 17l6-6 4 4 8-8|M15 7h6v6',
  barsUp:    'M4 20h16|M6.5 16v-5|M10.5 16V6|M14.5 16v-7|M18.5 16V4',
  gauge:     'M4 16a8 8 0 0116 0|M12 16l4.5-4.5|M6.5 19.5h11',
  battery:   'M3 8h15v8H3z|M21 11v2|M6 11v2|M9.5 11v2|M13 11v2',
  strip:     'M3 9h18v6H3z|M9 9v6|M15 9v6',
  briefcase: 'M4 8h16v11H4z|M9 8V5h6v3|M4 13h16',
  people:    'M9 5a3 3 0 100 6 3 3 0 000-6z|M3.5 20a5.5 5.5 0 0111 0|M17 6.6a2.4 2.4 0 100 4.8 2.4 2.4 0 000-4.8z|M15.5 14.6A4.5 4.5 0 0121 19',
  // Mission Control 2.0 (Entry 536): the approved canvas's own glyphs (mc-kit icons) —
  // hearts are how an agent levels up, and "working now" is a pulse line.
  heart:     'M12 20s-7.5-4.6-9.2-9.3C1.6 7.4 3.7 4.5 6.9 4.5c2.1 0 3.5 1.2 5.1 3 1.6-1.8 3-3 5.1-3 3.2 0 5.3 2.9 4.1 6.2C19.5 15.4 12 20 12 20z',
  activity:  'M3 12h4l3-8 4 16 3-8h4',
  // The Weekly Kickoff (Design System 2.0 Part 2 #9): a win, a week nobody held, and the opening.
  trophy:    'M8 4h8v6a4 4 0 01-8 0z|M8 6H5v1.5A3.5 3.5 0 008.3 11|M16 6h3v1.5A3.5 3.5 0 0115.7 11|M12 14v3.5|M8.5 21h7|M9.5 17.5h5V21h-5z',
  skip:      'M5.6 5.6l12.8 12.8|' + pc(12, 12, 9),
  sun:       pc(12, 12, 4) + '|M12 2v2|M12 20v2|M4.9 4.9l1.4 1.4|M17.7 17.7l1.4 1.4|M2 12h2|M20 12h2|M4.9 19.1l1.4-1.4|M17.7 6.3l1.4-1.4',
} as const;

export type V2IconName = keyof typeof PI | keyof typeof RIC;

/** `PIcon` from the design: `name` resolves PI first, then the rail set. */
export function PIcon({
  name,
  d,
  size = 16,
  color = 'currentColor',
  sw = 1.7,
  style,
}: {
  name?: V2IconName;
  d?: string;
  size?: number;
  color?: string;
  sw?: number;
  style?: React.CSSProperties;
}): React.ReactElement {
  const path = d || (name && ((PI as Record<string, string>)[name] || (RIC as Record<string, string>)[name])) || '';
  return <RailIcon d={path} size={size} color={color} sw={sw} style={style} />;
}

export default PIcon;
